import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Truck, Headphones, ShoppingBag, Bookmark, ChevronLeft } from 'lucide-react';
import { useSaves } from '../../contexts/SavesContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRegion } from '../../contexts/RegionContext';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const features = [
  { icon: <Truck size={28} strokeWidth={1.5} />, title: 'Free Setup', desc: 'Complimentary installation and onboarding included.' },
  { icon: <Headphones size={28} strokeWidth={1.5} />, title: 'Specialist Support', desc: 'Connect with an ARK specialist for your space.' },
  { icon: <ShoppingBag size={28} strokeWidth={1.5} />, title: 'Enterprise Licensing', desc: 'Flexible plans for museums and institutions.' },
];

const ShopArk = () => {
  const navigate = useNavigate();
  const { toggleSave, isSaved } = useSaves();
  const { isAuthenticated } = useAuth();
  const { region, formatPrice } = useRegion();

  const products = [
    {
      id: 'ark',
      badge: 'NEW',
      name: 'FOZZIL ARK',
      tagline: 'Intelligence. Contained.',
      description: 'Plug-and-play AI Museum with T.A.E.L.S Core, S.H.A.D.E. Vision, and Living Ancestor Engine.',
      image: process.env.PUBLIC_URL + '/ark-product.png',
      chipLabel: 'T.A.E.L.S Core',
      specs: ['18T ops/sec', '8K HDMI 2.1', '100+ Languages', 'On-device AI'],
      price: `From ${formatPrice(region.ark.min)}`,
      priceNote: `${formatPrice(region.ark.min)} – ${formatPrice(region.ark.max)}`,
      path: '/shop/buy-ark/ark',
    },
    {
      id: 'ark-pro',
      badge: 'NEW',
      name: 'FOZZIL ARK Pro',
      tagline: 'Pro performance. Pro possibilities.',
      description: 'Enhanced ARK with multi-canvas 360° projection, advanced S.H.A.D.E., dual Neural Vault, and enterprise networking.',
      image: process.env.PUBLIC_URL + '/ark-pro-product.png',
      chipLabel: 'Intelligence Core Pro',
      specs: ['36T ops/sec', 'Multi 8K Output', '360° Projection', 'Dual Neural Vault'],
      price: `From ${formatPrice(region.arkPro.min)}`,
      priceNote: `${formatPrice(region.arkPro.min)} – ${formatPrice(region.arkPro.max)}`,
      path: '/shop/buy-ark/ark-pro',
    },
  ];

  return (
    <div className="store-page bg-white dark:bg-black text-[#1d1d1f] dark:text-[#f5f5f7] transition-colors duration-400 min-h-screen">
      {/* Breadcrumb */}
      <div className="max-w-[1120px] mx-auto px-6 py-4">
        <Link to="/shop" className="inline-flex items-center gap-1 text-[#065DC0] text-sm font-medium hover:underline">
          <ChevronLeft size={16} /> Back to Store
        </Link>
      </div>

      {/* Hero */}
      <section className="py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <motion.h1 variants={fadeInUp} className="text-[clamp(40px,6vw,64px)] font-bold tracking-tight leading-none">
              Shop ARK
            </motion.h1>            
          </motion.div>
        </div>
      </section>

      {/* All Models */}
      <section className="py-12 md:py-16">
        <div className="max-w-[1120px] mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="text-[clamp(24px,3.5vw,36px)] font-bold tracking-tight mb-8">
              All models. <span className="text-[#6e6e73] dark:text-[#a1a1a6] font-medium">Take your pick.</span>
            </motion.h2>

            <div className="grid grid-cols-2 md:grid-cols-2 gap-3 sm:gap-4 md:gap-5 mt-4 sm:mt-6">
              {products.map((p) => (
                <motion.div key={p.id} variants={fadeInUp}>
                  <div
                    onClick={() => navigate(p.path)}
                    className="relative bg-white dark:bg-zinc-950 border border-black/[0.08] dark:border-white/[0.08] rounded-[20px] md:rounded-[32px] p-4 sm:p-6 lg:p-8 flex flex-col cursor-pointer transition-all duration-500 hover:border-blue-500/50 shadow-sm dark:shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] group overflow-hidden h-full"
                  >
                        {/* Artistic Background Elements */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.03] to-transparent pointer-events-none" />
                        <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#065DC0]/5 blur-[80px] rounded-full pointer-events-none" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isAuthenticated) {
                              navigate('/auth', { state: { from: { pathname: '/shop/buy-ark' } } });
                              return;
                            }
                            toggleSave({ ...p, link: p.path });
                          }}
                          className={`absolute top-3 right-3 sm:top-4 sm:right-4 z-[60] p-1.5 sm:p-2 rounded-full transition-all duration-300 ${isSaved(p.id) ? 'bg-[#065DC0] text-white shadow-lg shadow-[#065DC0]/30' : 'bg-black/5 dark:bg-white/5 text-[#6e6e73] dark:text-[#a1a1a6] hover:bg-black/10 dark:hover:bg-white/10'}`}
                        >
                          <Bookmark size={14} className="sm:hidden" fill={isSaved(p.id) ? 'currentColor' : 'none'} strokeWidth={isSaved(p.id) ? 0 : 2} />
                          <Bookmark size={16} className="hidden sm:block" fill={isSaved(p.id) ? 'currentColor' : 'none'} strokeWidth={isSaved(p.id) ? 0 : 2} />
                        </button>
                        <div className="hidden dark:block absolute inset-0 bg-gradient-to-t from-[#010308] via-black/40 to-transparent z-10 pointer-events-none" />
                        <div className="absolute inset-0 z-0 bg-blue-500/5 dark:bg-blue-600/20 blur-2xl sm:blur-[80px] rounded-full -translate-y-1/2 opacity-0 group-hover:opacity-100 dark:group-hover:opacity-30 transition-opacity duration-1000 pointer-events-none" />
                        
                        <div className="relative z-20 flex flex-col h-full">
                    {p.badge && (
                      <span className="text-[9px] sm:text-[10px] md:text-xs font-bold text-[#065DC0] uppercase tracking-[0.15em] mb-2 sm:mb-4">{p.badge}</span>
                    )}

                    {/* Logo instead of large image */}
                    <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-[32px] bg-white dark:bg-black flex items-center justify-center mb-3 sm:mb-5 group-hover:scale-110 transition-transform duration-700 border border-black/[0.06] dark:border-white/[0.1] relative overflow-hidden p-0">
                      <div className="absolute inset-0 bg-blue-500/5 dark:bg-blue-400/10 pointer-events-none" />
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover drop-shadow-md" />
                    </div>

                    <h3 className="text-sm sm:text-base md:text-xl lg:text-2xl font-bold tracking-tight mb-0.5 sm:mb-1 group-hover:text-[#065DC0] dark:group-hover:text-blue-400 transition-colors uppercase">{p.name}</h3>
                    <p className="text-[10px] sm:text-[11px] md:text-xs lg:text-sm font-light text-[#6e6e73] dark:text-zinc-400 group-hover:text-[#1d1d1f] dark:group-hover:text-zinc-200 transition-colors mb-3 sm:mb-4">{p.tagline}</p>

                    <div className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[9px] sm:text-[10px] md:text-xs font-semibold bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#6e6e73] dark:text-[#a1a1a6] border border-black/[0.08] dark:border-white/[0.08] mb-3 sm:mb-4 w-fit">
                      <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-[#065DC0]" />
                      {p.chipLabel}
                    </div>

                    {/* Compact Specs */}
                    <div className="flex flex-col mb-4 sm:mb-5 w-full flex-1">
                      {p.specs.map((s, i) => (
                        <div key={i} className="flex items-center gap-1.5 sm:gap-2 py-1 sm:py-1.5 md:py-2 border-b border-black/[0.04] dark:border-white/[0.08] text-[9px] sm:text-[10px] md:text-xs text-[#86868b] dark:text-[#6e6e73]">
                           <span className="w-1 h-1 rounded-full bg-blue-500 shrink-0" />
                           <span className="font-medium">{s}</span>
                        </div>
                      ))}
                    </div>

                    <p className="text-xs sm:text-sm md:text-[15px] font-bold mb-0.5 sm:mb-1">{p.price}</p>
                    <p className="text-[9px] sm:text-[10px] md:text-xs text-[#86868b] dark:text-[#6e6e73] mb-4 sm:mb-5">{p.priceNote}</p>

                    {/* CTA - Single Line */}
                    <div className="flex items-center gap-1.5 sm:gap-2 mt-auto pt-3 md:pt-4 border-t border-black/[0.06] dark:border-white/5 w-full">
                      <Link to={p.path} onClick={(e) => e.stopPropagation()} className="flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-[#065DC0] hover:bg-[#0077ed] text-white font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all border-none flex items-center justify-center">
                        Buy
                      </Link>
                      <Link to="/fozzil-ark" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-transparent hover:bg-[#065DC0] hover:text-white font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all border border-[#065DC0] text-[#065DC0] dark:hover:text-white flex items-center justify-center">
                        Learn more
                      </Link>
                    </div>
                  </div>
                </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Strip */}
      <section className="py-12 bg-white dark:bg-black transition-colors duration-400">
        <div className="max-w-[1120px] mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger} className="grid grid-cols-2 md:grid-cols-3 gap-3.5 sm:gap-6">
            {features.map((f, i) => (
              <motion.div key={i} variants={fadeInUp} className={`text-center px-3 sm:px-4 py-4 sm:py-8 rounded-[20px] sm:rounded-[28px] md:rounded-[32px] bg-white dark:bg-[#050505] border border-black/[0.06] dark:border-white/[0.08] shadow-sm hover:border-blue-500/30 transition-colors duration-500 ${
                i === 2 ? "col-span-2 md:col-span-1" : ""
              }`}>
                <div className="text-[#065DC0] dark:text-blue-400 mb-3 flex justify-center drop-shadow-sm">{f.icon}</div>
                <h3 className="text-sm sm:text-base md:text-[17px] font-bold text-[#1d1d1f] dark:text-white mb-2">{f.title}</h3>
                <p className="text-xs sm:text-sm text-[#6e6e73] dark:text-zinc-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 md:py-20">
        <div className="max-w-[1120px] mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-50px' }} variants={stagger}>
            <motion.h2 variants={fadeInUp} className="text-[clamp(24px,3.5vw,36px)] font-bold tracking-tight mb-10">
              Which ARK is right for you?
            </motion.h2>

            <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-3.5 sm:gap-6">
              {[
                {
                  name: 'FOZZIL ARK', tagline: 'Great for single exhibits and small museums.',
                  image: process.env.PUBLIC_URL + '/ark-product.png', path: '/shop/buy-ark/ark',
                  price: `From ${formatPrice(region.ark.min)}`,
                  specs: [
                    ['Chip', 'Intelligence Core'], ['Performance', '18T ops/sec'],
                    ['Output', 'Single 8K HDMI 2.1'], ['Security', 'Neural Vault'], ['Languages', '100+'],
                  ],
                },
                {
                  name: 'FOZZIL ARK Pro', tagline: 'Built for large museums and 360° experiences.',
                  image: process.env.PUBLIC_URL + '/ark-pro-product.png', path: '/shop/buy-ark/ark-pro',
                  price: `From ${formatPrice(region.arkPro.min)}`,
                  specs: [
                    ['Chip', 'Intelligence Core Pro'], ['Performance', '36T ops/sec'],
                    ['Output', 'Multi 8K HDMI 2.1'], ['Security', 'Dual Neural Vault'], ['Languages', '100+'],
                  ],
                },
              ].map((item) => (
                <div key={item.name} className="relative bg-white dark:bg-[#050505] border border-black/[0.08] dark:border-white/[0.08] rounded-[20px] md:rounded-[32px] p-4 sm:p-6 lg:p-8 flex flex-col cursor-pointer transition-all duration-500 hover:border-blue-500/50 shadow-sm dark:shadow-[0_0_15px_rgba(0,0,0,0.5)] group overflow-hidden">
                    <div className="hidden dark:block absolute inset-0 bg-gradient-to-t from-[#010308] via-black/40 to-transparent z-10 pointer-events-none" />
                    <div className="absolute inset-0 z-0 bg-blue-500/5 dark:bg-blue-600/20 blur-2xl sm:blur-[80px] rounded-full -translate-y-1/2 opacity-0 group-hover:opacity-100 dark:group-hover:opacity-30 transition-opacity duration-1000 pointer-events-none" />

                    <div className="relative z-20 flex flex-col h-full w-full">
                      <div className="flex flex-col items-center text-center">
                        {/* Compact Logo */}
                        <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-[32px] bg-white dark:bg-black flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-700 border border-black/[0.06] dark:border-white/[0.1] relative overflow-hidden p-0">
                          <div className="absolute inset-0 bg-blue-500/5 dark:bg-blue-400/10 pointer-events-none" />
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover drop-shadow-md" />
                        </div>
                        <h3 className="text-sm sm:text-base md:text-xl lg:text-2xl font-bold tracking-tight mb-0.5 sm:mb-1 group-hover:text-[#065DC0] dark:group-hover:text-blue-400 transition-colors uppercase">{item.name}</h3>
                        <p className="text-[10px] sm:text-[11px] md:text-xs lg:text-sm font-light text-[#6e6e73] dark:text-zinc-400 group-hover:text-[#1d1d1f] dark:group-hover:text-zinc-200 transition-colors mb-2 sm:mb-3">{item.tagline}</p>
                        <p className="text-xs sm:text-sm md:text-[15px] font-bold text-[#065DC0] mb-5 sm:mb-6">{item.price}</p>
                      </div>

                      <div className="flex flex-col mb-6 sm:mb-8 w-full flex-1">
                        {item.specs.map(([label, value]) => (
                          <div key={label} className="flex justify-between py-2 sm:py-2.5 border-b border-black/[0.04] dark:border-white/5 text-[9px] sm:text-[10px] md:text-xs text-left">
                            <span className="text-[#6e6e73] dark:text-zinc-500 font-medium">{label}</span>
                            <span className="font-semibold text-[#1d1d1f] dark:text-zinc-200 text-right">{value}</span>
                          </div>
                        ))}
                      </div>

                      {/* CTA - Single Line */}
                      <div className="flex items-center gap-1.5 sm:gap-2 mt-auto pt-3 md:pt-4 border-t border-black/[0.06] dark:border-white/5 w-full">
                        <Link to={item.path} onClick={(e) => e.stopPropagation()} className="flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-[#065DC0] hover:bg-[#0077ed] text-white font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all border-none flex items-center justify-center">
                          Buy
                        </Link>
                        <Link to="/fozzil-ark" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex-1 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-transparent hover:bg-[#065DC0] hover:text-white font-bold text-[8px] sm:text-[9px] md:text-xs uppercase tracking-wider transition-all border border-[#065DC0] text-[#065DC0] dark:hover:text-white flex items-center justify-center">
                          Learn more
                        </Link>
                      </div>
                    </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ShopArk;
